import React, { useState } from 'react';
import { Button } from 'src/components/ui/Button';
import { CheckCircle as CheckIcon } from '@mui/icons-material';
import { OrgPermission, availablePermissions } from '@/constants/organization.constants';
import type { DemoCustomRole } from '@/types/demo.types';

interface RoleModalProps {
  isDemo: boolean;
  orgId: string | undefined;
  editingRole: DemoCustomRole | null;
  onClose: () => void;
  onSave: (
    name: string,
    description: string | undefined,
    permissions: OrgPermission[],
    customRoleId?: string
  ) => Promise<void>;
}

export const RoleModal: React.FC<RoleModalProps> = ({
  isDemo,
  orgId,
  editingRole,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    name: editingRole?.name || '',
    description: editingRole?.description || '',
    permissions: editingRole?.permissions || []
  });
  const [isSaving, setIsSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const handlePermissionToggle = (permissionKey: string) => {
    const permission = permissionKey as OrgPermission;
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const handleSave = async () => {
    if (!orgId) return;

    setIsSaving(true);
    setModalError(null);

    try {
      await onSave(
        formData.name.trim(),
        formData.description.trim() || undefined,
        formData.permissions as OrgPermission[],
        editingRole?.customRoleId
      );
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save role';
      setModalError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setModalError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-900 border border-white/10 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white text-xl font-semibold">
            {editingRole ? 'Edit Role' : 'Create Custom Role'}
          </h2>
          <button
            onClick={handleClose}
            className="text-white/40 hover:text-white"
          >
            ✕
          </button>
        </div>

        {modalError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {modalError}
          </div>
        )}

        <div className="space-y-6">
          {/* Role name */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Role Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Paralegal, Contract Manager"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-white/30"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what this role is responsible for..."
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 resize-none"
            />
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-white text-sm font-medium mb-3">
              Permissions ({formData.permissions.length} selected)
            </label>
            <div className="space-y-3">
              {availablePermissions.map((permission) => (
                <div
                  key={permission.key}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    formData.permissions.includes(permission.key)
                      ? 'border-purple-500/50 bg-purple-500/10'
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}
                  onClick={() => handlePermissionToggle(permission.key)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                      formData.permissions.includes(permission.key)
                        ? 'border-purple-500 bg-purple-500'
                        : 'border-white/30'
                    }`}>
                      {formData.permissions.includes(permission.key) && (
                        <CheckIcon className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div>
                      <h4 className="text-white font-medium">{permission.label}</h4>
                      <p className="text-white/40 text-sm">{permission.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-8">
          <Button
            onClick={handleClose}
            variant="ghost"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="default"
            className="flex-1"
            disabled={!formData.name.trim() || formData.permissions.length === 0 || isSaving}
          >
            {isSaving ? 'Saving...' : editingRole ? 'Update Role' : 'Create Role'}
          </Button>
        </div>
      </div>
    </div>
  );
};
